/*
 * Linked add-on (snippets/product-linked-addon.liquid)
 *
 * When an add-on is selected, it is added to the cart in the same /cart/add request as the product:
 * - UpCart handles add to cart on this store and posts JSON { items: [...] } through window.fetch,
 *   so fetch is wrapped to append the add-on to that request.
 * - If UpCart isn't handling it, the theme's Alpine form posts instead; addToCart is swapped on that
 *   form to send the product and add-on together.
 * The add-on row is read from the product page or quick view the add button was clicked in, and only
 * requests containing that form's selected variant get the add-on. The displayed price includes the
 * selected add-on, and a matching add-on image replaces the variant image in the gallery. Loaded on every
 * page (layout) so it also covers quick view; it does nothing where there is no add-on row.
 */
(function () {
  if (window.linkedAddonInitialized) return;
  window.linkedAddonInitialized = true;

  const selectors = {
    context: '[data-quickview-modal], .product-section',
    row: '[data-linked-addon]',
    input: '[data-linked-addon-input]',
    valueDisplay: '[data-option-value]',
    productForm: 'product-form',
    variantIdInput: 'form[data-product-form] input[name="id"]',
    productJson: 'form[data-product-form] [data-product-json]',
    addButton: '[data-add-to-cart]',
    priceWrapper: '[data-price-wrapper]',
    productPrice: '[data-product-price]',
    compareAtAmount: '.product__price--compare-at[data-price-off-amount]',
    addonImages: '[data-linked-addon-images]',
    quickView: '[data-quickview-modal]',
    slideshow: '[data-product-slideshow]',
    galleryImage: '[data-swipe-gallery] img, [data-product-slideshow] img, [data-product-thumbs] img',
  };

  // Product page or quick view whose add button was clicked last
  let activeContext = null;

  function getActiveRow() {
    if (activeContext && document.contains(activeContext)) {
      return activeContext.querySelector(selectors.row);
    }
    // No click recorded (e.g. submitted with the keyboard): only safe when there's a single row
    const rows = document.querySelectorAll(selectors.row);
    return rows.length === 1 ? rows[0] : null;
  }

  function getSelectedAddonId(row) {
    const checked = row.querySelector(`${selectors.input}:checked`);
    return checked && checked.value ? parseInt(checked.value, 10) : null;
  }

  function getMainVariantId(row) {
    const context = row.closest(selectors.context);
    const idInput = context && context.querySelector(selectors.variantIdInput);
    return idInput ? parseInt(idInput.value, 10) : null;
  }

  // Returns the items array with the add-on appended, or null if this request shouldn't get it
  function withAddon(items) {
    const row = getActiveRow();
    const addonId = row && getSelectedAddonId(row);
    if (!addonId || !Array.isArray(items)) return null;

    const mainVariantId = getMainVariantId(row);
    if (!mainVariantId) return null;

    const mainItem = items.find((item) => item && parseInt(item.id, 10) === mainVariantId);
    const hasAddon = items.some((item) => item && parseInt(item.id, 10) === addonId);
    if (!mainItem || hasAddon) return null;

    const productTitle = row.dataset.linkedAddonProduct || '';
    return items.concat({
      id: addonId,
      quantity: parseInt(mainItem.quantity, 10) || 1,
      properties: productTitle ? { '_Add-on for': productTitle } : {},
    });
  }

  function isCartAddRequest(url, init) {
    const method = ((init && init.method) || 'GET').toUpperCase();
    return method === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(url);
  }

  // UpCart path: JSON { items } posted through window.fetch
  const originalFetch = window.fetch;
  window.fetch = function (resource, init) {
    try {
      const url = resource instanceof Request ? resource.url : String(resource);
      if (isCartAddRequest(url, init) && typeof init.body === 'string') {
        const body = JSON.parse(init.body);
        const items = withAddon(body.items);
        if (items) {
          init = Object.assign({}, init, { body: JSON.stringify(Object.assign({}, body, { items })) });
        }
      }
    } catch (error) {
      // Not a JSON add request, send it unchanged
    }
    return originalFetch.call(this, resource, init);
  };

  // Theme path: productAddButtonForm posts url-encoded form data, so send JSON { items } instead
  function formDataToItem(formData) {
    const item = { properties: {} };
    for (const [name, value] of formData.entries()) {
      const property = /^properties\[(.*)\]$/.exec(name);
      if (name === 'id') item.id = parseInt(value, 10);
      else if (name === 'quantity') item.quantity = parseInt(value, 10) || 1;
      else if (name === 'selling_plan' && value) item.selling_plan = value;
      else if (property) item.properties[property[1]] = value;
    }
    item.quantity = item.quantity || 1;
    return item;
  }

  function addToCartWithAddon(formData) {
    const mainItem = formDataToItem(formData);
    const items = withAddon([mainItem]) || [mainItem];

    return window
      .fetch(`${window.theme.routes.cart}/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items }),
      })
      .then((response) =>
        response
          .json()
          .catch(() => ({}))
          .then((data) => {
            // Match the shape theme.js expects: resolve { data: line item }, reject { data: error }
            if (!response.ok) throw { data };
            const lines = (data && data.items) || [];
            const mainLine = lines.find((line) => line && line.variant_id === mainItem.id) || lines[0];
            if (!mainLine) throw { data: {} };
            return { data: mainLine };
          })
      )
      .catch((error) => {
        // Network or unexpected errors get the theme's generic error message instead of an uncaught rejection
        throw error && error.data ? error : { data: {} };
      });
  }

  // Capture phase so this runs before UpCart and the theme handle the click
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest(selectors.addButton) : null;
      if (!button) return;
      activeContext = button.closest(selectors.context);
      if (!activeContext || !activeContext.querySelector(selectors.row) || !window.Alpine) return;

      const form = button.closest(selectors.productForm);
      if (!form || form.linkedAddonInstalled) return;
      const component = window.Alpine.$data(form);
      if (!component || typeof component.addToCart !== 'function') return;

      component.addToCart = addToCartWithAddon;
      form.linkedAddonInstalled = true;
    },
    true
  );

  // Displayed price = selected variant price + selected add-on price. Always recalculated from the
  // variant, so repeated updates never stack. Formatting mirrors theme.js formattingMoney.
  function formatMoney(cents) {
    const format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    const withDelimiters = (number, precision, thousands, decimal) => {
      const [whole, fraction] = (number / 100).toFixed(precision).split('.');
      const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    const formatted = format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
      switch (placeholder) {
        case 'amount_no_decimals':
          return withDelimiters(cents, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(cents, 2, "'", '.');
        default:
          return withDelimiters(cents, 2, ',', '.');
      }
    });
    const showCode = window.theme && window.theme.settings && window.theme.settings.currency_code_enable;
    return showCode && window.theme.currencyCode ? `${formatted} ${window.theme.currencyCode}` : formatted;
  }

  function getCurrentVariant(context) {
    const idInput = context.querySelector(selectors.variantIdInput);
    const productJson = context.querySelector(selectors.productJson);
    if (!idInput || !productJson) return null;
    try {
      const product = JSON.parse(productJson.textContent);
      const variantId = parseInt(idInput.value, 10);
      return (product.variants || []).find((variant) => variant && variant.id === variantId) || null;
    } catch (error) {
      return null;
    }
  }

  function updatePrice(context, variant) {
    const row = context && context.querySelector(selectors.row);
    if (!row) return;
    // Selling plan prices are handled by the theme; leave them alone
    if (context.querySelector('[name="selling_plan"]:checked:not([value=""])')) return;

    const currentVariant = variant || getCurrentVariant(context);
    if (!currentVariant || typeof currentVariant.price !== 'number') return;

    const checked = row.querySelector(`${selectors.input}:checked`);
    const addonPrice = (checked && parseInt(checked.dataset.linkedAddonPrice, 10)) || 0;
    const onSale = currentVariant.compare_at_price > currentVariant.price;
    const showsCompareAt = window.theme && window.theme.settings && window.theme.settings.badge_sale_type === 'compare_at_price';

    context.querySelectorAll(selectors.priceWrapper).forEach((wrapper) => {
      const priceEl = wrapper.querySelector(selectors.productPrice);
      if (priceEl) priceEl.textContent = formatMoney(currentVariant.price + addonPrice);

      const compareEl = wrapper.querySelector(selectors.compareAtAmount);
      if (compareEl && onSale && showsCompareAt) compareEl.textContent = formatMoney(currentVariant.compare_at_price + addonPrice);
    });
  }

  /*
   * Gallery images for the selected add-on. Add-on images are product media with alt text like
   * "variation | Iron | 5-50lbs | Power Bench" (see snippets/linked-addon-image-check.liquid). The galleries
   * don't render them; instead, when an add-on is selected, the best match is shown in place of the current
   * variant's image wherever the gallery shows it (v1 swipe gallery, v2 slideshow, thumbnails, quick view).
   * Each "|" part is one option; commas list alternatives ("90\", 80\", 70\"" = any of those heights). A match
   * needs one part to include the selected add-on and every part to have one of its values selected; the
   * image with the most parts wins (then the fewest alternatives). No match, or the default button, shows
   * the variant image as usual.
   */
  const imageSwapKeys = ['src', 'srcset', 'sizes'];

  // Compare values like Shopify handles, so capitals, extra spaces and symbols in alt text don't break a
  // match: "Power  Bench", "power-bench" and " POWER BENCH " all become "power-bench"
  function normalizeValue(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function fileName(url) {
    return String(url || '').split('?')[0].split('/').pop();
  }

  function getAddonImages(row) {
    if (row.linkedAddonImages) return row.linkedAddonImages;
    let images = [];
    try {
      const data = JSON.parse((row.querySelector(selectors.addonImages) || {}).textContent || '[]');
      images = (Array.isArray(data) ? data : [])
        .filter((image) => image && typeof image.src === 'string' && image.src && typeof image.alt === 'string')
        .map((image) => ({
          src: image.src,
          srcset: typeof image.srcset === 'string' ? image.srcset : '',
          // Drop the leading "variation"; each part is a list of alternatives
          parts: image.alt
            .split('|')
            .slice(1)
            .map((part) => part.split(',').map(normalizeValue).filter(Boolean))
            .filter((part) => part.length),
        }))
        .filter((image) => image.parts.length)
        .map((image) => Object.assign(image, { alternatives: image.parts.reduce((total, part) => total + part.length, 0) }));
    } catch (error) {
      images = [];
    }
    row.linkedAddonImages = images;
    return images;
  }

  function findAddonImage(row, variant) {
    const checked = row.querySelector(`${selectors.input}:checked`);
    if (!checked || !checked.value) return null;

    const addonValue = normalizeValue(checked.dataset.linkedAddonText);
    if (!addonValue) return null;

    const selected = new Set((variant.options || []).map(normalizeValue).concat(addonValue));
    let best = null;
    getAddonImages(row).forEach((image) => {
      const matches =
        image.parts.some((part) => part.includes(addonValue)) &&
        image.parts.every((part) => part.some((value) => selected.has(value)));
      if (!matches) return;
      const moreSpecific =
        !best ||
        image.parts.length > best.parts.length ||
        (image.parts.length === best.parts.length && image.alternatives < best.alternatives);
      if (moreSpecific) best = image;
    });
    return best;
  }

  function getVariantImageSrc(variant) {
    const media = variant && variant.featured_media;
    if (media && media.preview_image && media.preview_image.src) return media.preview_image.src;
    return (variant && variant.featured_image && variant.featured_image.src) || '';
  }

  function getPictureSources(img) {
    const picture = img.parentElement && img.parentElement.tagName === 'PICTURE' ? img.parentElement : null;
    return picture ? Array.from(picture.querySelectorAll('source')) : [];
  }

  function setImage(img, values) {
    imageSwapKeys.forEach((key) => {
      if (values[key]) img.setAttribute(key, values[key]);
      else img.removeAttribute(key);
    });
    // <source> elements in a <picture> take priority over the <img>, so they need the same image
    getPictureSources(img).forEach((source, index) => {
      const srcset = Array.isArray(values.sources) ? values.sources[index] : values.srcset || values.src;
      if (srcset) source.setAttribute('srcset', srcset);
    });
  }

  function restoreImages(context) {
    (context.linkedAddonSwapped || []).forEach((img) => {
      // Only restore if the theme hasn't already replaced it (e.g. v1 swaps the first slide on variant change)
      if (img.getAttribute('src') === img.dataset.linkedAddonSwapSrc) {
        try {
          setImage(img, JSON.parse(img.dataset.linkedAddonOriginal || '{}') || {});
        } catch (error) {
          // Original couldn't be read; leave the image as it is
        }
      }
      delete img.dataset.linkedAddonSwapSrc;
      delete img.dataset.linkedAddonOriginal;
    });
    context.linkedAddonSwapped = [];
  }

  function updateImage(context, variant, showInSlideshow) {
    const row = context && context.querySelector(selectors.row);
    if (!row) return;
    restoreImages(context);

    const currentVariant = variant || getCurrentVariant(context);
    const variantImage = fileName(getVariantImageSrc(currentVariant));
    const addonImage = currentVariant && variantImage ? findAddonImage(row, currentVariant) : null;
    if (!addonImage) return;

    // Quick view lists every image and never scrolls to the variant's image, so use the top image there;
    // elsewhere replace the image(s) currently showing the variant image
    const isQuickView = context.matches(selectors.quickView);
    const targets = isQuickView
      ? [context.querySelector(`${selectors.slideshow} img`)].filter(Boolean)
      : Array.from(context.querySelectorAll(selectors.galleryImage)).filter(
          (img) => fileName(img.getAttribute('src')) === variantImage
        );

    const newValues = { src: addonImage.src, srcset: addonImage.srcset, sizes: '' };
    targets.forEach((img) => {
      const original = { sources: getPictureSources(img).map((source) => source.getAttribute('srcset') || '') };
      imageSwapKeys.forEach((key) => {
        original[key] = img.getAttribute(key) || '';
      });
      newValues.sizes = original.sizes;
      img.dataset.linkedAddonOriginal = JSON.stringify(original);
      img.dataset.linkedAddonSwapSrc = addonImage.src;
      setImage(img, newValues);
      context.linkedAddonSwapped.push(img);
    });

    // Bring the variant slide into view when the add-on is picked (the theme does this on variant change)
    const slideshow = showInSlideshow && !isQuickView && context.querySelector(selectors.slideshow);
    if (slideshow && currentVariant.featured_media && currentVariant.featured_media.id) {
      slideshow.dispatchEvent(new CustomEvent('theme:image:change', { detail: { id: currentVariant.featured_media.id } }));
    }
  }

  // Theme updates prices and images on variant change, then fires this; re-apply the add-on on top
  document.addEventListener('theme:variant:change', (event) => {
    const context = event.target instanceof Element ? event.target.closest(selectors.context) : null;
    if (!context) return;
    const variant = event.detail && event.detail.variant;
    updatePrice(context, variant);
    updateImage(context, variant, false);
  });

  document.addEventListener('change', (event) => {
    const input = event.target instanceof Element ? event.target.closest(selectors.input) : null;
    const row = input && input.closest(selectors.row);
    if (!row) return;

    // Keep the selected value in the row heading in sync (hover text is handled by the theme's swapper)
    const display = row.querySelector(selectors.valueDisplay);
    if (display) display.textContent = input.dataset.linkedAddonText || '';

    const context = row.closest(selectors.context);
    updatePrice(context);
    updateImage(context, null, true);
  });
})();
